<template>
  <div v-if="value" class="im-chat-panel">
    <div class="im-chat-panel__header">
      <span class="im-chat-panel__title">{{ headerTitle }}</span>
      <i class="el-icon-close im-chat-panel__close" @click="close"></i>
    </div>
    <div class="im-chat-panel__body">
      <im-client v-if="ready"></im-client>
      <div v-else class="im-chat-panel__loading">
        <i class="el-icon-loading"></i>
        <span>{{ $t('message.home.连接中...') }}</span>
      </div>
    </div>
    <!-- 用户信息 / 大图预览浮层 -->
    <user-info v-show="uiStore.userInfo.show" :pos="uiStore.userInfo.pos" :user="uiStore.userInfo.user"
      @close="$store.commit('closeUserInfoBox')"></user-info>
    <full-image :visible="uiStore.fullImage.show" :url="uiStore.fullImage.url"
      @close="$store.commit('closeFullImageBox')"></full-image>
  </div>
</template>

<script>
import ImClient from '../view/Client.vue';
import UserInfo from './common/UserInfo.vue';
import FullImage from './common/FullImage.vue';
import { IM_WS_URL } from '@/config';
import { BIZ_TOKEN, BIZ_USERINFO } from '@/common/constant';

// IM 全局只初始化一次（WebSocket 连接、离线消息），跨多个商品详情页共享
let imInited = false;

export default {
  name: 'ImChatPanel',
  components: { ImClient, UserInfo, FullImage },
  props: {
    // v-model 控制显示
    value: { type: Boolean, default: false },
    // 私聊目标商家 { id, showName/nickName, headImage }
    target: { type: Object, default: null }
  },
  data() {
    return {
      ready: false,
      lastPlayAudioTime: new Date().getTime() - 1000
    };
  },
  computed: {
    uiStore() {
      return this.$store.state.uiStore;
    },
    headerTitle() {
      const chat = this.$store.state.chatStore.activeChat;
      if (chat && chat.showName) return chat.showName;
      if (this.target) return this.target.showName || this.target.nickName || this.target.name || '';
      return '在线客服';
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.openPanel();
      }
    }
  },
  methods: {
    /** 读取用户PC端登录态 */
    readToken() {
      return localStorage.getItem(BIZ_TOKEN) || sessionStorage.getItem(BIZ_TOKEN) || '';
    },
    readUserId() {
      const raw = sessionStorage.getItem(BIZ_USERINFO) || localStorage.getItem(BIZ_USERINFO);
      if (!raw) return '';
      try {
        return JSON.parse(raw).id || '';
      } catch (e) {
        return '';
      }
    },
    openPanel() {
      this.ensureInit().then(() => {
        this.ready = true;
        this.openTargetChat();
      }).catch((e) => {
        console.error('IM 初始化失败', e);
        this.$message && this.$message.error('消息服务连接失败');
      });
    },
    /** 初始化 IM：加载当前用户、建立 WebSocket（全局仅一次） */
    ensureInit() {
      if (imInited) {
        return Promise.resolve();
      }
      const token = this.readToken();
      const userId = this.readUserId();
      if (!token) {
        return Promise.reject(new Error('未登录'));
      }
      return this.$store.dispatch('loadUser').then(() => {
        this.$wsApi.connect(IM_WS_URL, token, userId);
        this.$wsApi.onConnect(() => {
          this.pullPrivateOfflineMessage(this.$store.state.chatStore.privateMsgMaxId);
        });
        this.$wsApi.onMessage((cmd, msgInfo) => {
          if (cmd == 2) {
            this.$wsApi.close(3000);
            this.$message && this.$message.error('您已在其他地方登录');
          } else if (cmd == 3) {
            this.handlePrivateMessage(msgInfo);
          } else if (cmd == 5) {
            this.handleSystemMessage(msgInfo);
          }
        });
        this.$wsApi.onClose((e) => {
          if (e && e.code != 3000) {
            this.$wsApi.reconnect(IM_WS_URL, this.readToken(), this.readUserId());
          }
        });
        imInited = true;
      });
    },
    /** 以当前 target 商家打开私聊会话 */
    openTargetChat() {
      if (!this.target || !this.target.id) {
        return;
      }
      const chatInfo = {
        type: 'PRIVATE',
        targetId: this.target.id,
        showName: this.target.showName || this.target.nickName || this.target.name || '',
        headImage: this.target.headImage || this.target.avatar || ''
      };
      this.$store.commit('openChat', chatInfo);
      const idx = this.$store.getters.findChatIdx(chatInfo);
      if (idx != null) {
        this.$store.commit('activeChat', Number(idx));
      }
    },
    pullPrivateOfflineMessage(minId) {
      this.$store.commit('loadingPrivateMsg', true);
      this.$http({
        url: '/message/private/pullOfflineMessage?minId=' + minId,
        method: 'GET'
      }).catch(() => {
        this.$store.commit('loadingPrivateMsg', false);
      });
    },
    handlePrivateMessage(msg) {
      // 消息加载标志
      if (msg.type == this.$enums.MESSAGE_TYPE.LOADING) {
        this.$store.commit('loadingPrivateMsg', JSON.parse(msg.content));
        return;
      }
      // 对方已读，清空已读数量
      if (msg.type == this.$enums.MESSAGE_TYPE.READED) {
        this.$store.commit('resetUnreadCount', { type: 'PRIVATE', targetId: msg.recvId });
        return;
      }
      // 消息回执，改消息状态为已读
      if (msg.type == this.$enums.MESSAGE_TYPE.RECEIPT) {
        this.$store.commit('readedMessage', { friendId: msg.sendId });
        return;
      }
      msg.selfSend = msg.sendId == this.$store.state.userStore.userInfo.id;
      const userId = msg.selfSend ? msg.recvId : msg.sendId;
      this.loadUserInfo(userId).then((user) => {
        this.insertPrivateMessage(user, msg);
      });
    },
    insertPrivateMessage(friend, msg) {
      const chatInfo = {
        type: 'PRIVATE',
        targetId: friend.id,
        showName: friend.nickName || friend.userName || friend.username,
        headImage: friend.headImage || friend.headImageThumb
      };
      this.$store.commit('openChat', chatInfo);
      this.$store.commit('insertMessage', [msg, chatInfo]);
      if (!msg.selfSend && this.$msgType.isNormal(msg.type) &&
        msg.status != this.$enums.MESSAGE_STATUS.READED) {
        this.playAudioTip();
      }
    },
    handleSystemMessage(msg) {
      if (msg.type == this.$enums.MESSAGE_TYPE.USER_BANNED) {
        this.$wsApi.close(3000);
        this.$message && this.$message.error('您的账号已被封禁: ' + (msg.content || ''));
      }
    },
    loadUserInfo(id) {
      return new Promise((resolve) => {
        const friend = this.$store.state.friendStore.friends.find((f) => f.id == id);
        if (friend) {
          resolve(friend);
        } else {
          this.$http({ url: '/user/find/' + id, method: 'get' }).then((user) => {
            this.$store.commit('addFriend', user);
            resolve(user);
          }).catch(() => {
            resolve({ id: id, nickName: '', headImage: '' });
          });
        }
      });
    },
    playAudioTip() {
      if (this.$store.getters.isLoading()) {
        return;
      }
      if (new Date().getTime() - this.lastPlayAudioTime > 1000) {
        this.lastPlayAudioTime = new Date().getTime();
        try {
          const audio = new Audio();
          audio.src = require('@/imchat/assets/audio/tip.wav');
          audio.play();
        } catch (e) {}
      }
    },
    close() {
      this.$emit('input', false);
    }
  }
}
</script>

<style lang="scss">
/* IM 全局样式与图标（仅随本组件加载） */
@import '../assets/style/im.scss';
@import '../assets/iconfont/iconfont.css';

.im-chat-panel {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 420px;
  height: 560px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 30px rgba(0, 0, 0, 0.25);
  z-index: 3000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    height: 44px;
    flex-shrink: 0;
    background: var(--im-color-primary, #2e7eff);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 14px;
    font-size: 14px;
  }

  &__close {
    cursor: pointer;
    font-size: 18px;
  }

  &__body {
    flex: 1;
    overflow: hidden;
    position: relative;

    .chat-page,
    .el-container {
      height: 100%;
    }
  }

  &__loading {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #999;

    i {
      font-size: 28px;
      margin-bottom: 8px;
    }
  }
}
</style>
